import { CwjTaskEngineError } from "../cwj-task-engine/types.js";
import { CwjWorkspaceError } from "./types.js";
class Mutex {
    constructor() {
        this.locked = false;
        this.queue = [];
    }
    acquire() {
        if (!this.locked) {
            this.locked = true;
            return Promise.resolve();
        }
        return new Promise((resolve) => this.queue.push(resolve));
    }
    release() {
        const next = this.queue.shift();
        if (next) {
            next();
        }
        else {
            this.locked = false;
        }
    }
}
function deepClone(value) {
    if (typeof structuredClone === "function") {
        return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value));
}
function cloneMap(map) {
    const cloned = new Map();
    for (const [key, value] of map.entries()) {
        cloned.set(key, deepClone(value));
    }
    return cloned;
}
function hashTaskInput(input) {
    const normalized = {
        title: input.title,
        description: input.description,
        priority: input.priority,
        requiresApproval: input.requiresApproval,
        expiresAt: input.expiresAt,
        maxDurationMs: input.maxDurationMs,
        payload: input.payload,
    };
    return JSON.stringify(normalized);
}
function hashWorkflowInput(input) {
    const normalized = {
        title: input.title,
        description: input.description,
        maxDurationMs: input.maxDurationMs,
        payload: input.payload,
    };
    return JSON.stringify(normalized);
}
function scopeKey(context, idempotencyKey) {
    return `${context.tenantId ?? ""}:${context.ownerId ?? ""}:${context.projectId}:${context.skillId}:${idempotencyKey}`;
}
/**
 * In-memory combined store for tasks and workflows used by CwjWorkspace.
 * It provides a serializable transaction boundary: `runExclusive` clones the
 * current state, applies the operation, and only commits if the operation
 * resolves. On rejection the cloned state is discarded, leaving the committed
 * maps unchanged.
 *
 * This is not a distributed transaction; it is an in-process atomic
 * snapshot/restore coordinator used by the workspace mutation coordinator.
 */
export class CwjWorkspaceTransactionStore {
    constructor() {
        this.tasks = new Map();
        this.workflows = new Map();
        this.idempotency = new Map();
        this.lock = new Mutex();
    }
    snapshot() {
        return {
            tasks: cloneMap(this.tasks),
            workflows: cloneMap(this.workflows),
            idempotency: cloneMap(this.idempotency),
        };
    }
    restore(snapshot) {
        this.tasks = cloneMap(snapshot.tasks);
        this.workflows = cloneMap(snapshot.workflows);
        this.idempotency = cloneMap(snapshot.idempotency);
    }
    async runExclusive(operation) {
        await this.lock.acquire();
        const committedSnapshot = this.snapshot();
        this.pending = this.snapshot();
        try {
            const result = await operation();
            if (this.pending) {
                this.tasks = this.pending.tasks;
                this.workflows = this.pending.workflows;
                this.idempotency = this.pending.idempotency;
            }
            this.pending = undefined;
            return result;
        }
        catch (error) {
            this.restore(committedSnapshot);
            this.pending = undefined;
            throw error;
        }
        finally {
            this.lock.release();
        }
    }
    asTaskStore() {
        return new WorkspaceTaskStore(this);
    }
    asWorkflowStore() {
        return new WorkspaceWorkflowStore(this);
    }
    // Internal methods used by the mutation coordinator and store adapters.
    activeTasks() {
        return this.pending?.tasks ?? this.tasks;
    }
    activeWorkflows() {
        return this.pending?.workflows ?? this.workflows;
    }
    activeIdempotency() {
        return this.pending?.idempotency ?? this.idempotency;
    }
    loadTask(taskId) {
        const task = this.activeTasks().get(taskId);
        return task ? deepClone(task) : undefined;
    }
    saveTask(task, expectedVersion) {
        const map = this.activeTasks();
        const existing = map.get(task.taskId);
        const currentVersion = existing?.version ?? 0;
        if (currentVersion !== expectedVersion) {
            throw new CwjTaskEngineError(`concurrency: expected task version ${expectedVersion}, found ${currentVersion}`, "concurrency");
        }
        map.set(task.taskId, deepClone(task));
    }
    deleteTask(taskId) {
        this.activeTasks().delete(taskId);
    }
    listTasks(filters) {
        let results = Array.from(this.activeTasks().values());
        if (!filters)
            return results.map((t) => deepClone(t));
        if (filters.projectId !== undefined) {
            results = results.filter((t) => t.context.projectId === filters.projectId);
        }
        if (filters.skillId !== undefined) {
            results = results.filter((t) => t.context.skillId === filters.skillId);
        }
        if (filters.tenantId !== undefined) {
            results = results.filter((t) => t.context.tenantId === filters.tenantId);
        }
        if (filters.ownerId !== undefined) {
            results = results.filter((t) => t.context.ownerId === filters.ownerId);
        }
        if (filters.status !== undefined) {
            results = results.filter((t) => t.status === filters.status);
        }
        if (filters.requestedBy !== undefined) {
            results = results.filter((t) => t.context.requestedBy === filters.requestedBy);
        }
        return results.map((t) => deepClone(t));
    }
    loadWorkflow(workflowId) {
        const workflow = this.activeWorkflows().get(workflowId);
        return workflow ? deepClone(workflow) : undefined;
    }
    saveWorkflow(workflow, expectedVersion) {
        const map = this.activeWorkflows();
        const existing = map.get(workflow.workflowId);
        const currentVersion = existing?.version ?? 0;
        if (currentVersion !== expectedVersion) {
            throw new CwjWorkspaceError(`concurrency: expected workflow version ${expectedVersion}, found ${currentVersion}`, "concurrency");
        }
        map.set(workflow.workflowId, deepClone(workflow));
    }
    deleteWorkflow(workflowId) {
        this.activeWorkflows().delete(workflowId);
    }
    listWorkflows(filters) {
        let results = Array.from(this.activeWorkflows().values());
        if (!filters)
            return results.map((w) => deepClone(w));
        if (filters.projectId !== undefined) {
            results = results.filter((w) => w.context.projectId === filters.projectId);
        }
        if (filters.skillId !== undefined) {
            results = results.filter((w) => w.context.skillId === filters.skillId);
        }
        if (filters.tenantId !== undefined) {
            results = results.filter((w) => w.context.tenantId === filters.tenantId);
        }
        if (filters.ownerId !== undefined) {
            results = results.filter((w) => w.context.ownerId === filters.ownerId);
        }
        if (filters.status !== undefined) {
            results = results.filter((w) => w.status === filters.status);
        }
        return results.map((w) => deepClone(w));
    }
    findTaskByIdempotencyKey(context, idempotencyKey) {
        const record = this.activeIdempotency().get(scopeKey(context, idempotencyKey));
        if (!record)
            return undefined;
        const task = this.activeTasks().get(record.taskId);
        if (!task) {
            throw new CwjWorkspaceError(`idempotency_incomplete: idempotency record exists but task ${record.taskId} is missing`, "idempotency_incomplete");
        }
        return deepClone(task);
    }
    findWorkflowByIdempotencyKey(context, idempotencyKey) {
        const record = this.activeIdempotency().get(scopeKey(context, idempotencyKey));
        if (!record)
            return undefined;
        const workflow = this.activeWorkflows().get(record.workflowId);
        if (!workflow) {
            throw new CwjWorkspaceError(`idempotency_incomplete: idempotency record exists but workflow ${record.workflowId} is missing`, "idempotency_incomplete");
        }
        return deepClone(workflow);
    }
    registerIdempotency(context, idempotencyKey, record) {
        this.activeIdempotency().set(scopeKey(context, idempotencyKey), deepClone(record));
    }
    getIdempotencyRecord(context, idempotencyKey) {
        const record = this.activeIdempotency().get(scopeKey(context, idempotencyKey));
        return record ? deepClone(record) : undefined;
    }
    hashTaskInput(input) {
        return hashTaskInput(input);
    }
    hashWorkflowInput(input) {
        return hashWorkflowInput(input);
    }
}
class WorkspaceTaskStore {
    constructor(store) {
        this.store = store;
    }
    async load(taskId) {
        return this.store.loadTask(taskId);
    }
    async save(task, expectedVersion) {
        this.store.saveTask(task, expectedVersion);
    }
    async list(filters) {
        return this.store.listTasks(filters);
    }
    async delete(taskId) {
        this.store.deleteTask(taskId);
    }
    async findByIdempotencyKey(context, idempotencyKey) {
        return this.store.findTaskByIdempotencyKey(context, idempotencyKey);
    }
}
class WorkspaceWorkflowStore {
    constructor(store) {
        this.store = store;
    }
    async load(workflowId) {
        return this.store.loadWorkflow(workflowId);
    }
    async save(workflow, expectedVersion) {
        this.store.saveWorkflow(workflow, expectedVersion);
    }
    async list(filters) {
        return this.store.listWorkflows(filters);
    }
    async delete(workflowId) {
        this.store.deleteWorkflow(workflowId);
    }
    async findByIdempotencyKey(context, idempotencyKey) {
        return this.store.findWorkflowByIdempotencyKey(context, idempotencyKey);
    }
}
//# sourceMappingURL=transactionStore.js.map